import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '~/components/FirebaseProvider'

export interface UploadProgress {
    progress: number
    downloadURL?: string
    error?: string
}

export const uploadFileToFirebase = async (
    file: File,
    path: string,
    onProgress?: (progress: number) => void
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const storageRef = ref(storage, path)
        const uploadTask = uploadBytesResumable(storageRef, file)

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                onProgress?.(progress)
            },
            (error) => {
                console.error('Upload error:', error)

                // Provide more specific error messages
                let errorMessage = 'Upload failed'
                if (error.code === 'storage/unauthorized') {
                    errorMessage = 'Authentication required. Please log in and try again.'
                } else if (error.code === 'storage/quota-exceeded') {
                    errorMessage = 'Storage quota exceeded. Please try with smaller files.'
                } else if (error.code === 'storage/retry-limit-exceeded') {
                    errorMessage = 'Upload failed due to network issues. Please try again.'
                }

                reject(new Error(errorMessage))
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref)
                    .then((downloadURL) => {
                        resolve(downloadURL)
                    })
                    .catch((error) => {
                        console.error('Error getting download URL:', error)
                        reject(new Error(error instanceof Error ? error.message : 'Unknown error'))
                    })
            }
        )
    })
}

export const uploadProviderPhotos = async (
    files: File[],
    providerId: string,
    onProgress?: (fileIndex: number, progress: number) => void
): Promise<Array<{ url: string; caption?: string }>> => {
    const uploadPromises = files.map(async (file, index) => {
        const timestamp = Date.now()
        const fileName = `${timestamp}_${file.name}`
        const path = `providers/${providerId}/photos/${fileName}`

        try {
            const url = await uploadFileToFirebase(
                file,
                path,
                (progress) => onProgress?.(index, progress)
            )

            return {
                url,
                caption: file.name,
            }
        } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error)
            throw error
        }
    })

    return Promise.all(uploadPromises)
}

export const generateUniqueFileName = (originalName: string): string => {
    const timestamp = Date.now()
    const randomString = Math.random().toString(36).substring(2, 15)
    const extension = originalName.split('.').pop() ?? 'jpg'
    return `${timestamp}_${randomString}.${extension}`
}

export const uploadCoverPhoto = async (
    file: File,
    userId: string,
    onProgress?: (progress: number) => void
): Promise<string> => {
    const timestamp = Date.now()
    const fileName = `cover_${timestamp}_${file.name}`
    const path = `users/${userId}/cover/${fileName}`

    try {
        const url = await uploadFileToFirebase(
            file,
            path,
            onProgress
        )

        return url
    } catch (error) {
        console.error(`Error uploading cover photo:`, error)
        throw error
    }
}

export const uploadResumePdf = async (
    file: File,
    providerId: string,
    onProgress?: (progress: number) => void
): Promise<string> => {
    const timestamp = Date.now()
    const fileName = `resume_${timestamp}_${file.name}`
    const path = `providers/${providerId}/resume/${fileName}`

    try {
        const url = await uploadFileToFirebase(
            file,
            path,
            onProgress
        )

        return url
    } catch (error) {
        console.error(`Error uploading resume PDF:`, error)
        throw error
    }
}

export const deleteFileFromFirebase = async (fileUrl: string): Promise<void> => {
    try {
        console.log('Attempting to delete file:', fileUrl)

        // Extract the file path from the Firebase Storage URL
        // Firebase Storage URLs look like: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile.jpg?alt=media&token=...
        const url = new URL(fileUrl)
        const pathMatch = /\/o\/(.+)/.exec(url.pathname)

        if (!pathMatch) {
            throw new Error('Invalid Firebase Storage URL')
        }

        // Decode the path (Firebase Storage URLs are URL-encoded)
        const filePath = decodeURIComponent(pathMatch[1] ?? '')

        if (!filePath) {
            throw new Error('Could not extract file path from URL')
        }

        console.log('Extracted file path:', filePath)

        // Create a reference to the file
        const fileRef = ref(storage, filePath)

        // Delete the file
        await deleteObject(fileRef)

        console.log('File deleted successfully:', filePath)
    } catch (error: unknown) {
        // If the file doesn't exist, that's fine - it's already deleted
        if (error && typeof error === 'object' && 'code' in error && error.code === 'storage/object-not-found') {
            console.log('File already deleted from Firebase Storage:', fileUrl)
            return
        }

        console.error('Error deleting file from Firebase Storage:', error)
        throw error
    }
} 